import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Hook to manage search dialog for MainLayout component
 * @param {boolean} searchDialogOpen - Search dialog open state
 * @param {string} searchQuery - Search query string
 * @param {Function} setSearchDialogOpen - Setter for search dialog open state
 * @param {Function} setSearchQuery - Setter for search query
 * @returns {Object} Search handlers
 */
export const useMainLayoutSearch = (
  searchDialogOpen,
  searchQuery,
  setSearchDialogOpen,
  setSearchQuery
) => {
  const navigate = useNavigate();

  const handleSearchClose = () => {
    setSearchDialogOpen(false);
    setSearchQuery('');
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      // Navigate to all queries page with search query
      navigate(`/dashboard/queries/all?search=${encodeURIComponent(searchQuery.trim())}`);
      handleSearchClose();
    }
  };

  const handleQuickSearch = (route) => {
    navigate(route);
    handleSearchClose();
  };

  // Keyboard shortcut for search (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchDialogOpen(true);
      }
      // Close search dialog on Escape
      if (e.key === 'Escape' && searchDialogOpen) {
        setSearchDialogOpen(false);
        setSearchQuery('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchDialogOpen, setSearchDialogOpen, setSearchQuery]);

  return {
    handleSearchClose,
    handleSearchChange,
    handleSearchSubmit,
    handleQuickSearch,
  };
};
